import { useCallback, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useVerifyAssessmentsUserMutation } from 'services/assessmentsService';
import { useAppDispatch } from 'store/store';
import {
  clearAssessments,
  setSaveCompleted,
  selectSaveAndCloseCompleted,
} from 'features/assessments/assessmentsSlice';
import { useSelector } from 'react-redux';
import { clearAssessmentsAuth } from 'features/assessments/assessmentsAuthSlice';
import { useAssessmentsAlertError } from './useAssessmentsAlertError';
import {
  isServerError,
  isClientError,
  isCredentialError,
  hasStatusCode,
} from '../utils/assessmentsErrorUtils';
import { transformVerifyUserRequest } from '../utils/apiDataTransformUtils';
import { useAssessmentsAuthConfig } from './useAssessmentsAuthConfig';
import { useAssessmentsParams } from './useAssessmentsParams';
import { setAssessmentsDetails } from '../assessmentsAuthSlice';
import { AssessmentStatus } from '../constants';
import { ASSESSMENTS_LOGIN_FAILURE_REASONS } from '../pendo/constants';
import { useAssessmentsLoginTracking } from '../pendo/useAssessmentsLoginTracking';

export const useAssessmentsAuth = () => {
  const { authenticationFields } = useAssessmentsAuthConfig();
  const { handleSubmit, ...formMethods } = useForm<FieldValues>();
  const { surveyEventId, tenantId } = useAssessmentsParams();
  const [verifyAssessmentsUser, { isLoading }] = useVerifyAssessmentsUserMutation();
  const { isShowingAlertError, alertError, showAlert } = useAssessmentsAlertError();
  const saveAndCloseCompleted = useSelector(selectSaveAndCloseCompleted);
  const dispatch = useAppDispatch();
  const { trackLoginSucceeded, trackLoginFailed } = useAssessmentsLoginTracking();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showNotFound, setShowNotFound] = useState<boolean>(false);

  const handleError = useCallback(
    (error: any) => {
      if (!hasStatusCode(error)) {
        trackLoginFailed(ASSESSMENTS_LOGIN_FAILURE_REASONS.NETWORK_ERROR);
        showAlert('Please check your network and try again.');
        return;
      }

      const { status } = error;
      if (isServerError(status)) {
        trackLoginFailed(ASSESSMENTS_LOGIN_FAILURE_REASONS.SERVER_ERROR, status);
        showAlert('Something went wrong. Please try again later.');
      } else if (isClientError(status)) {
        setShowNotFound(true);
      } else if (isCredentialError(status)) {
        trackLoginFailed(ASSESSMENTS_LOGIN_FAILURE_REASONS.INVALID_CREDENTIALS, status);
        const backendMessage =
          (error as any).data?.message || 'Invalid credentials. Please try again.';
        setErrorMessage(backendMessage);
      }
    },
    [showAlert, trackLoginFailed],
  );

  const onSubmit = useCallback(
    (fieldData: FieldValues) => {
      if (!tenantId || !surveyEventId) return;
      setErrorMessage('');
      setShowNotFound(false);
      verifyAssessmentsUser({
        tenantId,
        requestBody: transformVerifyUserRequest({
          authFields: authenticationFields ?? [],
          authdata: fieldData,
          surveyEventId,
        }),
      }).then(({ data, error }) => {
        if (error) {
          handleError(error);
          return;
        }

        dispatch(
          setAssessmentsDetails({
            accessToken: data.accessToken,
            activityId: data.activityId,
            assessmentsId: data.assessmentsId,
            clientPatientId: data.clientPatientId,
            firstName: data.firstName,
          }),
        );

        trackLoginSucceeded({
          assessmentsId: data.assessmentsId,
          activityId: data.activityId,
        });

        const alreadyCompleted = data.assessmentStatus === AssessmentStatus.completed;
        if (alreadyCompleted) {
          dispatch(setSaveCompleted(true));
        }
      });
    },
    [
      authenticationFields,
      surveyEventId,
      tenantId,
      verifyAssessmentsUser,
      dispatch,
      handleError,
      trackLoginSucceeded,
    ],
  );

  const logoutAssessments = useCallback(() => {
    dispatch(clearAssessmentsAuth());
    dispatch(clearAssessments());
  }, [dispatch]);

  return {
    onSubmit: handleSubmit(onSubmit),
    formMethods,
    isSubmitting: isLoading,
    logoutAssessments,
    errorMessage,
    showNotFound,
    isShowingAlertError,
    alertError,
    saveAndCloseCompleted,
  };
};
